/**
 * Material Requests — per-project requests with multiple line items,
 * approval workflow, and per-item delivery status.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/materialRequests/types.ts
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/materialRequests/materialRequests.ts
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/materialRequests/materialApproval.ts
 *
 * Mobile + web read AND write the same `materialRequests/{requestId}`
 * docs. The on-write Cloud Function (`approvalNotifications`) handles
 * push notifications to approvers — no extra work on web.
 *
 * Auto-approval rule: Super Admin / Admin / Manager → request is
 * created as `approved` (no approver needed). Others → `pending`.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { canApproveMaterial } from '@shared/perms';

import { db } from '@/lib/firebase';
import type { RoleKey } from '@/lib/useProjectMembers';

// ───── Types ─────

export type MaterialRequestStatus =
  | 'draft'
  | 'pending'
  | 'approved'
  | 'rejected';

export type DeliveryStatus =
  | 'pending'
  | 'ordered'
  | 'delivered'
  | 'received_at_site';

export const DELIVERY_STATUSES: {
  key: DeliveryStatus;
  label: string;
  icon: string;
  color: string;
}[] = [
  { key: 'pending', label: 'Pending', icon: 'ph ph-clock', color: '#92400E' },
  { key: 'ordered', label: 'Ordered', icon: 'ph ph-shopping-cart', color: '#1D4ED8' },
  { key: 'delivered', label: 'Delivered', icon: 'ph ph-package', color: '#7C3AED' },
  { key: 'received_at_site', label: 'At Site', icon: 'ph ph-check-circle', color: '#047857' },
];

export function getDeliveryStatusLabel(s: DeliveryStatus): string {
  return DELIVERY_STATUSES.find((x) => x.key === s)?.label ?? s;
}

export type MaterialRequestItem = {
  /** FK to materialLibrary item if picked from library. Optional —
   *  freehand items have no libraryItemId. */
  libraryItemId?: string;
  category?: string;
  name: string;
  brand: string;
  variety: string;
  make: string;
  size: string;
  unit: string;
  quantity: number;
  rate: number;
  totalCost: number;
  deliveryStatus: DeliveryStatus;
};

export type MaterialRequest = {
  id: string;
  orgId: string;
  projectId: string;
  title: string;
  status: MaterialRequestStatus;
  items: MaterialRequestItem[];
  totalValue: number;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
  approvedBy?: string;
  approvedAt?: { toMillis(): number } | null;
  rejectionNote?: string;
  rejectedBy?: string;
  rejectedAt?: { toMillis(): number } | null;
  autoApproved?: boolean;
  designatedApproverUids?: string[];
  createdByRole?: RoleKey;
  editedAt?: { toMillis(): number } | null;
  editedBy?: string;
  lastDeliveryUpdateAt?: { toMillis(): number } | null;
  lastDeliveryUpdateBy?: string;
};

// ───── Auto-approve gate (mirrors mobile) ─────

export function materialAutoApprovesOnCreate(role: RoleKey | undefined): boolean {
  return canApproveMaterial(role);
}

// ───── Writes ─────

export type CreateMaterialRequestInput = {
  orgId: string;
  projectId: string;
  title: string;
  items: MaterialRequestItem[];
  createdBy: string;
  creatorRole: RoleKey;
  designatedApproverUids?: string[];
};

export async function createMaterialRequest(
  input: CreateMaterialRequestInput,
): Promise<string> {
  const totalValue = input.items.reduce((s, i) => s + i.totalCost, 0);
  const title =
    input.title ||
    `Request #${Date.now().toString(36).slice(-4).toUpperCase()}`;
  const base: Record<string, unknown> = {
    orgId: input.orgId,
    projectId: input.projectId,
    title,
    items: input.items,
    totalValue,
    createdBy: input.createdBy,
    createdByRole: input.creatorRole,
    createdAt: serverTimestamp(),
  };
  if (materialAutoApprovesOnCreate(input.creatorRole)) {
    const ref = await addDoc(collection(db, 'materialRequests'), {
      ...base,
      status: 'approved',
      approvedBy: input.createdBy,
      approvedAt: serverTimestamp(),
      autoApproved: true,
    });
    return ref.id;
  }
  const designated = input.designatedApproverUids?.filter(Boolean) ?? [];
  const ref = await addDoc(collection(db, 'materialRequests'), {
    ...base,
    status: 'pending',
    ...(designated.length > 0
      ? { designatedApproverUids: designated }
      : {}),
  });
  return ref.id;
}

export type UpdateMaterialRequestInput = {
  requestId: string;
  title: string;
  items: MaterialRequestItem[];
  editedBy: string;
};

export async function updateMaterialRequest(
  input: UpdateMaterialRequestInput,
): Promise<void> {
  const totalValue = input.items.reduce((s, i) => s + i.totalCost, 0);
  await updateDoc(doc(db, 'materialRequests', input.requestId), {
    title:
      input.title ||
      `Request #${Date.now().toString(36).slice(-4).toUpperCase()}`,
    items: input.items,
    totalValue,
    updatedAt: serverTimestamp(),
    editedAt: serverTimestamp(),
    editedBy: input.editedBy,
  });
}

/** Push a rejected request back into the approval queue. */
export async function resubmitRejectedRequest(input: {
  requestId: string;
  title: string;
  items: MaterialRequestItem[];
  editedBy: string;
}): Promise<void> {
  const totalValue = input.items.reduce((s, i) => s + i.totalCost, 0);
  await updateDoc(doc(db, 'materialRequests', input.requestId), {
    title:
      input.title ||
      `Request #${Date.now().toString(36).slice(-4).toUpperCase()}`,
    items: input.items,
    totalValue,
    status: 'pending',
    rejectionNote: deleteField(),
    updatedAt: serverTimestamp(),
    editedAt: serverTimestamp(),
    editedBy: input.editedBy,
  });
}

export async function approveRequest(
  requestId: string,
  approvedBy: string,
): Promise<void> {
  await updateDoc(doc(db, 'materialRequests', requestId), {
    status: 'approved',
    approvedBy,
    approvedAt: serverTimestamp(),
  });
}

export async function rejectRequest(
  requestId: string,
  rejectedBy: string,
  rejectionNote: string,
): Promise<void> {
  await updateDoc(doc(db, 'materialRequests', requestId), {
    status: 'rejected',
    rejectionNote,
    rejectedBy,
    rejectedAt: serverTimestamp(),
  });
}

export async function updateItemDeliveryStatus(
  requestId: string,
  itemIndex: number,
  status: DeliveryStatus,
  updatedBy: string,
): Promise<void> {
  const snap = await getDoc(doc(db, 'materialRequests', requestId));
  const data = snap.data() as
    | { items?: MaterialRequestItem[] }
    | undefined;
  if (!data?.items) return;
  const items = [...data.items];
  if (itemIndex < 0 || itemIndex >= items.length) return;
  if (items[itemIndex].deliveryStatus === status) return;
  items[itemIndex] = { ...items[itemIndex], deliveryStatus: status };
  await updateDoc(doc(db, 'materialRequests', requestId), {
    items,
    lastDeliveryUpdateAt: serverTimestamp(),
    lastDeliveryUpdateBy: updatedBy,
  });
}

export async function deleteMaterialRequest(id: string): Promise<void> {
  await deleteDoc(doc(db, 'materialRequests', id));
}

// ───── Helpers ─────

export function emptyItem(unit = 'pcs'): MaterialRequestItem {
  return {
    name: '',
    brand: '',
    variety: '',
    make: '',
    size: '',
    unit,
    quantity: 0,
    rate: 0,
    totalCost: 0,
    deliveryStatus: 'pending',
  };
}

export function itemFromLibrary(
  lib: {
    id: string;
    category: string;
    name: string;
    brand: string;
    variety: string;
    make: string;
    size: string;
    unit: string;
    defaultRate?: number;
  },
  quantity = 1,
): MaterialRequestItem {
  const rate = lib.defaultRate ?? 0;
  return {
    libraryItemId: lib.id,
    category: lib.category,
    name: lib.name,
    brand: lib.brand,
    variety: lib.variety,
    make: lib.make,
    size: lib.size,
    unit: lib.unit,
    quantity,
    rate,
    totalCost: quantity * rate,
    deliveryStatus: 'pending',
  };
}
